// Named saved views for the filtered lists (doc-316 contracts; doc-356 extended
// the same menu to TPRM requests and document reviews).
//
// A saved view IS a querystring. Each list already knows how to reconstruct its
// controls from one — that list's seedFromParams is the single definition, and
// useUrlSyncedFilters re-runs it whenever the URL changes from outside the hook.
// So "apply a view" is a router navigation and nothing more; there is
// deliberately no second apply path that pokes the setters, which is exactly the
// drift useUrlSyncedFilters warns about (it would silently lose doc-194's
// option-list validation, doc-168's trimmed search and doc-140's deadline-window
// fallback).
//
// PER-SURFACE STORAGE. Every function takes the storage key as its FIRST
// argument rather than closing over one constant, because three lists now share
// this module and a view saved on Contracts must never show up on TPRM. The keys
// live in SAVED_VIEW_KEYS below; nothing else in this file knows a key by name.
//
// v1 storage is localStorage: per-browser, no cross-device sync, no backend and
// no migration. The UI says so out loud rather than letting the user discover it
// on their second laptop.
//
// Every function here is pure-ish (localStorage in, plain data out) and React
// free, so the rules below are unit-testable without mounting anything.

// The contracts key is LOAD-BEARING and must stay byte-identical: it has been
// live in real browsers since doc-316 shipped, there is no migration, and
// renaming it would silently destroy every view the legal team has already
// saved. savedViews.test.js pins the literal string for exactly that reason.
export const SAVED_VIEW_KEYS = {
  contracts: 'contractsSavedViews',
  tprm: 'tprmSavedViews',
  reviews: 'reviewsSavedViews',
};

export const MAX_VIEWS = 12;
export const MAX_NAME_LENGTH = 60;

// A name is trimmed and hard-capped. The input carries maxLength={MAX_NAME_LENGTH}
// so the UI cannot produce a longer one; this truncation is the defensive half,
// for a value that arrived some other way (a hand-edited storage key).
export function normalizeViewName(name) {
  return String(name ?? '').trim().slice(0, MAX_NAME_LENGTH);
}

// Case-insensitive, because "Expiring EU" and "expiring eu" are the same view to
// a human and two rows in the menu would be a bug, not a feature.
function sameName(a, b) {
  return normalizeViewName(a).toLowerCase() === normalizeViewName(b).toLowerCase();
}

export function findViewByName(views, name) {
  return (views || []).find((view) => sameName(view.name, name)) || null;
}

// A view is a FILTER SET, not a scroll position: re-opening "Expiring · EU
// entity" on page 3 is wrong. Dropping `page` also keeps applying a view clear
// of the re-seed page-installation subtleties in useUrlSyncedFilters
// (useReseedPageGuard) — the applied URL never carries a page > 1.
//
// Everything else rides along as-is, so a view captures exactly what its list
// URL-syncs and no more. On Contracts that includes sort ("newest first" is
// legitimately part of my view); on TPRM and Reviews sort is not URL-synced at
// all, so a view there is filters only — which is why no copy in the menu
// promises to restore your sort order.
export function stripPageParam(qs) {
  const params = new URLSearchParams(String(qs ?? '').replace(/^\?/, ''));
  params.delete('page');
  return params.toString();
}

function newId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `view-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// One bad entry (or one corrupt key) must never white-screen a list, so this
// validates instead of trusting — the shape getStoredPageSize uses. A missing
// key, invalid JSON, a non-array or a non-object entry all resolve to "no saved
// views"; a single malformed entry is dropped and its siblings survive.
export function listSavedViews(storageKey) {
  let raw;
  try {
    raw = localStorage.getItem(storageKey);
  } catch {
    // Reading can throw too (Safari with storage blocked entirely).
    return [];
  }
  if (!raw) return [];
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((entry) => (
      entry
      && typeof entry === 'object'
      && typeof entry.id === 'string' && entry.id
      && typeof entry.name === 'string' && entry.name.trim()
      && typeof entry.qs === 'string'
    ))
    .slice(0, MAX_VIEWS)
    .map((entry) => ({
      id: entry.id,
      name: normalizeViewName(entry.name),
      qs: stripPageParam(entry.qs),
    }));
}

// Persisting is the one operation that can fail for reasons outside our control
// (Safari private mode, quota). Every mutation below funnels through here and
// reports the failure as a value, so a caller can toast it and change nothing
// else — nothing throws out of this module.
function persist(storageKey, views) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(views));
    return { ok: true, views };
  } catch {
    return { ok: false, error: 'write-failed', views: listSavedViews(storageKey) };
  }
}

// Result shape for all three mutations: { ok, views, error? }. `views` is always
// the list the caller should now render — the new one on success, the unchanged
// stored one on failure.
export function saveView(storageKey, name, qs) {
  const cleanName = normalizeViewName(name);
  if (!cleanName) return { ok: false, error: 'empty-name', views: listSavedViews(storageKey) };
  const views = listSavedViews(storageKey);
  const existing = findViewByName(views, cleanName);
  const cleanQs = stripPageParam(qs);
  // A repeated name OVERWRITES (the menu confirms first) rather than creating a
  // second row with the same label. The id and the position are kept, so the
  // menu does not reshuffle under the user's cursor.
  if (existing) {
    return persist(storageKey, views.map((view) => (
      view.id === existing.id ? { ...view, name: cleanName, qs: cleanQs } : view
    )));
  }
  if (views.length >= MAX_VIEWS) return { ok: false, error: 'limit', views };
  return persist(storageKey, [...views, { id: newId(), name: cleanName, qs: cleanQs }]);
}

export function renameView(storageKey, id, name) {
  const cleanName = normalizeViewName(name);
  if (!cleanName) return { ok: false, error: 'empty-name', views: listSavedViews(storageKey) };
  const views = listSavedViews(storageKey);
  const target = views.find((view) => view.id === id);
  if (!target) return { ok: false, error: 'not-found', views };
  // Renaming ONTO another view's name would create the duplicate that saveView
  // exists to prevent. Rejected rather than silently merging two filter sets.
  const clash = findViewByName(views, cleanName);
  if (clash && clash.id !== id) return { ok: false, error: 'duplicate-name', views };
  return persist(storageKey, views.map((view) => (view.id === id ? { ...view, name: cleanName } : view)));
}

export function deleteView(storageKey, id) {
  const views = listSavedViews(storageKey);
  const next = views.filter((view) => view.id !== id);
  if (next.length === views.length) return { ok: false, error: 'not-found', views };
  return persist(storageKey, next);
}

export const SAVE_ERROR_MESSAGES = {
  'empty-name': 'Give the view a name.',
  'duplicate-name': 'Another view already uses that name.',
  limit: `You can keep up to ${MAX_VIEWS} saved views — delete one first.`,
  'not-found': 'That view is no longer saved in this browser.',
  'write-failed': "This browser wouldn't let Docket save the view (private mode or storage full).",
};
